import React from "react";
import style from "./PrerequisiteBlock.module.scss";
import { Controller } from "react-hook-form";
import { SelectBox } from "../../../../../primary/SelectBox/selectBox";
import { PAIN_SCALE, PHOTO_RECENCY_WINDOW } from "../debridementConfig";
import { TriStateAnswer } from "./TriStateAnswer";

export const PrerequisiteBlock = ({
  prerequisites,
  careSetting,
  answers,
  readiness,
  flagCount,
  open,
  setReadyOpen,
  onAnswer,
  onSelection,
  onReason,
  reasonGaps,
  showReasonErrors,
  disabled,
  register,
  errors,
  control,
}) => (
  <section className={`${style.form_section} ${disabled ? style.form_section_off : ""}`}>
    <button type="button" className={style.form_section_head} aria-expanded={open} data-testid="prerequisite-toggle" onClick={() => setReadyOpen(!open)}>
      <span className={style.form_section_number}>1</span>
      <span className={style.form_section_title}>Prerequisites</span>
      <span className={style.form_section_status}>
        {readiness.answered}/{readiness.total} answered
        {flagCount > 0 && ` · ${flagCount} flag${flagCount > 1 ? "s" : ""}`}
      </span>
      <span className={style.form_section_chevron}>&#9662;</span>
    </button>

    {open && (
      <div className={style.form_section_body}>
        {prerequisites.map((p) => {
          const answer = answers?.[p.key] || {};
          const note = p.settingNotes?.[careSetting];
          const missingReason = showReasonErrors && reasonGaps.includes(p.key);

          return (
            <div className={style.form_field} key={p.key} data-testid={`prereq-${p.key}`}>
              <div className={style.form_field_label}>
                <span>
                  {p.label} {careSetting === "hospital" && <span className={style.form_astrick}>*</span>}
                </span>
                {note && <div className={style.prereq_note}>{note}</div>}
              </div>

              <div className={style.form_field_control}>
                {p.control === "select" && (
                  <SelectBox
                    id={`prereq-${p.key}`}
                    name={`prereq-${p.key}`}
                    optionValue={p.options}
                    defaultValue={answer.selection || ""}
                    placeholder="Select…"
                    disabled={disabled}
                    classes={{ inputClassName: style.custom_basic_select }}
                    onChange={(e) => onSelection(p.key, e.target.value)}
                    validator={register({
                      required: (careSetting === "hospital" && !disabled) ? "This field is required" : false,
                    })}
                    errors={errors}
                  />
                )}

                {p.control === "pain" && (
                  <div className={style.prereq_stack}>
                    <SelectBox
                      id={`prereq-${p.key}`}
                      name={`prereq-${p.key}`}
                      optionValue={PAIN_SCALE}
                      defaultValue={answer.selection || ""}
                      placeholder="Select…"
                      disabled={disabled}
                      classes={{ inputClassName: style.custom_basic_select }}
                      onChange={(e) => onSelection(p.key, e.target.value)}
                      validator={register({
                        required: (careSetting === "hospital" && !disabled) ? "This field is required" : false,
                      })}
                      errors={errors}
                    />
                    <TriStateAnswer name={p.key} value={answer.value} disabled={disabled} onChange={(v) => onAnswer(p.key, v)} />
                  </div>
                )}

                {p.control === "photo" && (
                  <div className={style.prereq_stack}>
                    <SelectBox
                      id={`prereq-${p.key}`}
                      name={`prereq-${p.key}`}
                      optionValue={p.options.map((o) =>
                        o.key === "withinWindow" ? { ...o, value: `Within window (${PHOTO_RECENCY_WINDOW[careSetting]})` } : o
                      )}
                      defaultValue={answer.selection || ""}
                      placeholder="Select…"
                      disabled={disabled}
                      classes={{ inputClassName: style.custom_basic_select }}
                      onChange={(e) => onSelection(p.key, e.target.value)}
                      validator={register({
                        required: (careSetting === "hospital" && !disabled) ? "This field is required" : false,
                      })}
                      errors={errors}
                    />
                    <TriStateAnswer name={p.key} value={answer.value} disabled={disabled} onChange={(v) => onAnswer(p.key, v)} />
                  </div>
                )}

                {p.control === "tristate" && (
                  <div className={style.prereq_stack}>
                    <Controller
                      name={p.key}
                      control={control}
                      defaultValue={answer.value || ""}
                      rules={{
                        required: (careSetting === "hospital" && !disabled) ? "This field is required" : false,
                      }}
                      render={({ value, onChange }) => (
                        <TriStateAnswer
                          name={p.key}
                          value={value}
                          disabled={disabled}
                          onChange={(v) => {
                            onChange(v);
                            onAnswer(p.key, v);
                          }}
                        />
                      )}
                    />

                    {errors[p.key] && <span className={style.form_error_message}>{errors[p.key].message}</span>}
                  </div>
                )}
              </div>

              {answer.value === "no" && (
                <div className={style.prereq_reason} data-testid={`reason-${p.key}`}>
                  <label htmlFor={`reason-select-${p.key}`}>Reason — required <span className={style.form_astrick}>*</span></label>
                  <SelectBox
                    id={`reason-select-${p.key}`}
                    name={`reason-select-${p.key}`}
                    optionValue={p.reasons}
                    defaultValue={answer.reasonCode || ""}
                    placeholder="Select…"
                    disabled={disabled}
                    classes={{
                      inputClassName: `${style.custom_basic_select} ${missingReason ? style.form_error_border : ""}`,
                      column: style.w50,
                    }}
                    onChange={(e) => {
                      const picked = p.reasons.find((r) => r.key === e.target.value);
                      onReason(p.key, picked?.key || null, picked?.value || null);
                    }}
                    validator={register({
                      required: "This field is required",
                    })}
                    errors={errors}
                  />
                  <p className={style.prereq_keepgoing}>Flagged, not blocked — everything below stays editable and the record still saves.</p>
                  {missingReason && <p className={style.form_error_message}>A reason is required before saving.</p>}
                </div>
              )}
            </div>
          );
        })}
      </div>
    )}
  </section>
);
